import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * A bot changelogjának közös darabjai (docs/changelog.md): a témák, a jelölések,
 * a szöveg-megjelenítő és egy update teste. A nyilvános oldal, egy update saját
 * oldala, a Dashboard kártyája és a szerkesztő előnézete UGYANEZT rajzolja — a
 * jóváhagyott látványterv (Claude Design vászon, „MortisEngine Changelog”) szerint.
 */

// ── A témák: a bot menüjének csoportjai + Loader / Website / Other ─────────────
// A sorrend a backend utils/changelog.js TOPICS listájával egyezik.
const ICON_PATHS = {
  autopilot: <><circle cx="12" cy="12" r="10" /><path d="m16.24 7.76-2.12 6.36-6.36 2.12 2.12-6.36z" /></>,
  farm: <><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></>,
  quests: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>,
  visuals: <><path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" /><circle cx="12" cy="12" r="3" /></>,
  tools: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />,
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  loader: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>,
  website: <><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></>,
  other: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="1.5" /></>,
};

export const TOPICS = [
  { id: 'autopilot', label: 'Autopilot', hint: 'Play, Editor, Combat, Party, Revive, Key farm' },
  { id: 'farm',      label: 'Farm',      hint: 'Loot, Bag, Melt, Bloodshed, Gather, Stats' },
  { id: 'quests',    label: 'Quests',    hint: 'Daily challenges, Fortune Hunters' },
  { id: 'visuals',   label: 'Visuals',   hint: 'Camera, Names, ESP' },
  { id: 'tools',     label: 'Tools',     hint: 'The TOOLS tab' },
  { id: 'settings',  label: 'Settings',  hint: 'The SETTINGS tab' },
  { id: 'loader',    label: 'Loader',    hint: 'Sync, auto restart, updates' },
  { id: 'website',   label: 'Website',   hint: 'This site and the Dashboard' },
  { id: 'other',     label: 'Other',     hint: 'Anything else' },
];
export const TOPIC_LABEL = Object.fromEntries(TOPICS.map(t => [t.id, t.label]));

// ── A jelölések ───────────────────────────────────────────────────────────────
// NEW zöld (új), IMPROVED lila (jobb), FIXED semleges (javítás), REMOVED piros.
export const MARKS = [
  { id: 'new',      label: 'New',      cls: 'bg-ok/10 text-ok border-ok/40' },
  { id: 'improved', label: 'Improved', cls: 'bg-brand-500/[0.12] text-brand-400 border-brand-500/45' },
  { id: 'fixed',    label: 'Fixed',    cls: 'bg-gray-800 text-gray-200 border-line-strong' },
  { id: 'removed',  label: 'Removed',  cls: 'bg-danger/10 text-danger border-danger/40' },
];
const MARK_BY_ID = Object.fromEntries(MARKS.map(m => [m.id, m]));

export function Icon({ children, className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true" className={`flex-shrink-0 ${className}`}>
      {children}
    </svg>
  );
}

export function TopicIcon({ topic, className = 'w-4 h-4' }) {
  return <Icon className={className}>{ICON_PATHS[topic] || ICON_PATHS.other}</Icon>;
}

export function MarkBadge({ mark, className = '' }) {
  const m = MARK_BY_ID[mark] || MARK_BY_ID.new;
  return (
    <span className={`inline-flex items-center justify-center w-[84px] flex-shrink-0 py-[3px] rounded-[3px] border
                      font-mono text-[11px] font-medium uppercase tracking-[0.08em] ${m.cls} ${className}`}>
      {m.label}
    </span>
  );
}

/**
 * A szöveg: **vastag** és `kód` — SEMMI MÁS. Nem HTML: a React minden más
 * karaktert szövegként ír ki, így egy `<` is `<`-ként látszik.
 */
export function Rich({ text }) {
  const out = [];
  const s = String(text || '');
  let i = 0, buf = '', bold = false, key = 0;
  const flush = () => {
    if (!buf) return;
    out.push(bold ? <b key={key++} className="font-semibold text-gray-50">{buf}</b> : <React.Fragment key={key++}>{buf}</React.Fragment>);
    buf = '';
  };
  while (i < s.length) {
    if (s.startsWith('**', i)) { flush(); bold = !bold; i += 2; continue; }
    if (s[i] === '`') {
      const end = s.indexOf('`', i + 1);
      if (end > i) {
        flush();
        out.push(
          <code key={key++} className="font-mono text-[12.5px] text-gray-100 bg-gray-800 border border-[#30343a]
                                       rounded-[3px] px-[5px] py-px">
            {s.slice(i + 1, end)}
          </code>
        );
        i = end + 1;
        continue;
      }
    }
    buf += s[i++];
  }
  flush();
  return <>{out}</>;
}

/** „Cloud routes — your routes on every PC” → „Cloud routes” (az állapotsávnak). */
export function shortTitle(title) {
  return String(title || '').split(/\s[—–-]\s|:/)[0] || String(title || '');
}

// Rögzített hónapnevek: az en-GB formázó az újabb böngészőkben „Sept”-et ír, a
// terv (és a weboldal többi dátuma) „Sep”-et.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** „3 Oct 2026” — a látogató saját naptára szerint. */
export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Egy update-ben szereplő részek: Bot · Loader · Website. */
export function partsOf(post) {
  const topics = new Set((post.sections || []).map(s => s.topic));
  const parts = [];
  if ([...topics].some(t => !['loader', 'website'].includes(t))) parts.push('Bot');
  if (topics.has('loader')) parts.push('Loader');
  if (topics.has('website')) parts.push('Website');
  return parts.join(' · ');
}

export function countItems(section) {
  return (section.blocks || []).filter(b => b.type === 'item').length;
}

// ── Nagyítás ──────────────────────────────────────────────────────────────────
export function Lightbox({ image, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={image.caption || 'Picture'}
         className="fixed inset-0 z-[245] bg-[#060708]/90 flex flex-col items-center justify-center p-4 sm:p-8 animate-fade-in"
         onClick={onClose}>
      <img src={image.url} alt={image.caption || ''}
           className="max-w-full max-h-[82vh] object-contain rounded-xl border border-line bg-bar"
           onClick={e => e.stopPropagation()} />
      <div className="mt-4 flex items-center gap-4 text-sm text-gray-300 max-w-3xl text-center">
        {image.caption && <span>{image.caption}</span>}
        <button type="button" onClick={onClose} className="btn-secondary h-9 px-3 py-0 text-[13px]">Close</button>
      </div>
    </div>
  );
}

function Figure({ block, onOpen }) {
  if (!block.url) return null;
  // A kép legfeljebb a SAJÁT méretében: egy kis képernyőkép a szövegoszlop
  // szélességére nagyítva elmosódna (mérve: 395 px-es kép ~900 px-en).
  // A felirat-sávnak legalább 320 px kell, különben a gomb a szövegre csúszik.
  const maxW = block.width ? Math.max(block.width + 2, 320) : undefined;
  return (
    <figure className="m-0 mt-1.5 mb-2.5 sm:ml-[100px] rounded-xl border border-line overflow-hidden bg-bar"
            style={maxW ? { maxWidth: maxW } : undefined}>
      <button type="button" onClick={() => onOpen(block)} className="block w-full cursor-zoom-in"
              aria-label={`Enlarge: ${block.caption || 'picture'}`}>
        <img src={block.url} alt={block.caption || ''} width={block.width || undefined} height={block.height || undefined}
             loading="lazy" className="block w-full h-auto" />
      </button>
      <figcaption className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-t border-line bg-gray-900
                             text-[13px] text-gray-400">
        <span className="min-w-0">{block.caption}</span>
        <button type="button" onClick={() => onOpen(block)}
                className="flex items-center gap-1.5 text-[12.5px] text-gray-400 hover:text-gray-100 transition-colors flex-shrink-0">
          <Icon className="w-[15px] h-[15px]"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /><path d="M11 8v6" /><path d="M8 11h6" /></Icon>
          Enlarge
        </button>
      </figcaption>
    </figure>
  );
}

export function TopicHead({ section, size = 'md' }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-xl border border-[#30343a] bg-gray-800 grid place-items-center text-gray-200 flex-shrink-0">
        <TopicIcon topic={section.topic} className="w-[17px] h-[17px]" />
      </span>
      <div className="min-w-0">
        <span className="font-mono text-xs font-medium uppercase tracking-[0.1em] text-brand-500">
          {TOPIC_LABEL[section.topic] || 'Other'}
        </span>
        {section.title && (
          <h3 className={`mt-0.5 font-semibold tracking-[-0.01em] text-white ${size === 'lg' ? 'text-[21px]' : 'text-[19px]'}`}>
            {section.title}
          </h3>
        )}
      </div>
    </div>
  );
}

/** A témák a soraikkal és képeikkel. `idPrefix`: az ugró-linkek horgonya. */
export function PostSections({ post, size = 'md', idPrefix = 't' }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      {post.sections.map((s, i) => (
        <section key={i} id={`${idPrefix}${i}`} className="scroll-mt-nav flex flex-col gap-3 pt-7 border-t border-line">
          <TopicHead section={s} size={size} />
          <div className="flex flex-col">
            {s.blocks.map((b, j) => b.type === 'image'
              ? <Figure key={j} block={b} onOpen={setOpen} />
              : (
                <div key={j} className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-start py-[11px] border-t border-gray-800 first:border-t-0">
                  <MarkBadge mark={b.mark} className="sm:mt-[3px]" />
                  <p className="m-0 min-w-0 text-[15px] leading-[1.6] text-[#c9ccd1]"><Rich text={b.text} /></p>
                </div>
              ))}
          </div>
        </section>
      ))}
      {open && <Lightbox image={open} onClose={() => setOpen(null)} />}
    </>
  );
}

export function WarnIcon({ className = 'w-[18px] h-[18px]' }) {
  return (
    <Icon className={className}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" />
    </Icon>
  );
}

/** A fej: verzió · dátum · részek, cím, összefoglaló, "Action needed". */
export function PostHeader({ post, titleSize = 'md', link = false }) {
  const size = titleSize === 'lg' ? 'text-[30px] sm:text-[38px]' : 'text-[26px] sm:text-[30px]';
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        {post.version && (
          <span className="font-mono text-xs text-gray-100 border border-line-strong rounded-[3px] px-[7px] py-0.5">{post.version}</span>
        )}
        <span className="font-mono text-[13px] text-gray-400">{post.publishedAt ? formatDate(post.publishedAt) : 'Draft'}</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-gray-500">{partsOf(post)}</span>
      </div>
      <h2 className={`m-0 font-semibold leading-[1.15] tracking-[-0.02em] text-white ${size}`}>
        {link ? <Link to={`/changelog/${post.slug}`} className="hover:text-brand-200 transition-colors">{post.title}</Link> : post.title}
      </h2>
      {post.summary && <p className="m-0 text-base leading-[1.6] text-gray-300 max-w-[72ch]">{post.summary}</p>}
      {post.action && (
        <div role="note" className="flex gap-3 items-start rounded-xl border border-warn/45 bg-warn/[0.08] px-4 py-3
                                     text-[14.5px] leading-normal text-gray-50">
          <span className="text-warn mt-px"><WarnIcon /></span>
          <div>
            <span className="block mb-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-warn">Action needed</span>
            {post.action}
          </div>
        </div>
      )}
    </>
  );
}

/** "In this update": ugrás a témákra. */
export function TopicJump({ post, idPrefix = 't' }) {
  if (post.sections.length < 2) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="panel-label mr-1">In this update</span>
      {post.sections.map((s, i) => (
        <a key={i} href={`#${idPrefix}${i}`}
           className="inline-flex items-center gap-[7px] h-[30px] px-3 rounded-lg border border-[#30343a] text-[13px] text-gray-300
                      hover:border-line-hover hover:text-white transition-colors">
          <TopicIcon topic={s.topic} className="w-3.5 h-3.5" />{TOPIC_LABEL[s.topic]}
        </a>
      ))}
    </div>
  );
}
